<template>
  <LcModal
    v-bind="{ isVisible, tracking }"
    :background-class="$style.settingsModalBackground"
    :close-triggers="['click-outside', 'escape']"
    transition="slide-down-lg"
    @close="$emit('close')"
  >
    <template #modal="{ setRef }">
      <LcCard
        :ref="setRef"
        body-class="wrap flex flex-col h-full p-none"
        class="settings-modal lc-modal__card lc-modal__card--large bg-secondary-secondary-bold text-left"
        :class="{
          scrolled: isScrolled,
        }"
        role="dialog"
      >
        <header class="header flex items-center justify-between pt-150 px-150">
          <div class="flex items-center gap-100">
            <LcIcon :name="icon" />
            <h1 class="headline-h5-headline">{{ headline }}</h1>
          </div>

          <LcButton
            icon="close"
            size="small"
            variant="tertiary"
            @click="$emit('close')"
          />
        </header>

        <div ref="bodyRef" class="body pt-050" @scroll="handleByScroll">
          <slot />
        </div>

        <footer
          v-if="$slots.footer"
          class="footer flex justify-between w-full py-150 px-200"
        >
          <slot name="footer" />
        </footer>
      </LcCard>
    </template>
  </LcModal>
</template>

<script lang="ts" setup>
import type {
  LcCardSlots,
  LcIconName,
  LcModalEmits,
  LcModalProps,
} from '@lottocom/frontend-components'
import type { TrackingImpressionProps } from '~~/layers/tracking/app/types/tracking'

type SettingsModalProps = Pick<LcModalProps, 'isVisible'> &
  TrackingImpressionProps & {
    headline: string
    icon: LcIconName
  }

type SettingsModalSlots = Pick<LcCardSlots, 'default' | 'footer'>

defineSlots<SettingsModalSlots>()
defineEmits<LcModalEmits>()
defineProps<SettingsModalProps>()

const bodyRef = ref<HTMLElement | null>(null)
const isScrolled = ref(false)

const handleByScroll = () => {
  if (!bodyRef.value) return
  isScrolled.value = bodyRef.value.scrollTop > 0
}
</script>

<style lang="scss" module>
.settings-modal-background {
  overflow: hidden;
  padding: 4rem;

  @include media-tablet {
    padding: 0;
    background: none;
    backdrop-filter: none;
  }
}
</style>

<style lang="scss" scoped>
.settings-modal {
  @include media-tablet {
    height: 100%;
    border-radius: 0;
  }

  :deep(.body) {
    flex: 1 1 auto;
    overflow-y: auto;
  }

  :deep(.footer) {
    position: sticky;
    bottom: 0;
    border-top: separator('line');
  }

  :deep(.header) {
    position: sticky;
    top: 0;
    padding-bottom: 0.5rem;
  }

  :deep(.wrap) {
    min-height: 65vh;
  }
}

.scrolled {
  :deep(.header) {
    z-index: 125;
    background-color: token('color/background/primary/primary');
    box-shadow: token('shadow/shadow/card/large');
  }
}
</style>
